'use client';

import { memo } from 'react';
import { Row, TransformedNewsItem, Section, Column } from '@/lib/types/pageBuilder';
import ColumnRenderer from './ColumnRenderer';
import SectionHeading from './SectionHeading';
import { DynamicTabInfo } from './TabBarSection';
import type { IncludeSectionApiData } from '@/lib/pageUtils';
import { normalizeApiEndpoint } from '@/lib/apiUtils';
interface RowRendererProps {
  row: Row;
  collectionsData: Map<string, TransformedNewsItem[]>;
  dynamicTabsMap?: Map<string, DynamicTabInfo[]>;
  includeSectionsData?: Map<string, Section>;
  includeSectionApiData?: IncludeSectionApiData;
  isLastRow?: boolean;
  parentColumnData?: TransformedNewsItem[]; // Data inherited from parent column's data_binding
  dataStartIndex?: number; // Start index for sequential data consumption
  /** Is this the first row on the page (for LCP priority) */
  isFirstRow?: boolean;
}

/**
 * Calculate how many items a component will consume
 */
function getComponentItemCount(component: {
  data_config?: {
    mode?: string;
    loop_count?: number;
  };
}): number {
  if (!component.data_config) return 1;
  if (component.data_config.mode === 'loop') {
    return component.data_config.loop_count || 1;
  }
  return 1;
}

/**
 * Calculate total items consumed by a column and its nested elements
 * Based on component loop_count (not data_binding.limit - that's for API calls)
 *
 * `includeSectionsData` lets an `includeSection` column (which embeds an
 * entire separate page-builder Section by slug, not a components/rows tree
 * of its own) count the *real* nested content instead of falling back to a
 * blind guess — the resolved Section is already fetched server-side before
 * this runs, so the lookup is synchronous.
 */
function getColumnItemCount(column: Column, includeSectionsData?: Map<string, Section>): number {
  let count = 0;
  if (column.components) {
    for (const comp of column.components) {
      count += getComponentItemCount(comp);
    }
  }
  if (column.columns) {
    for (const col of column.columns) {
      count += getColumnItemCount(col, includeSectionsData);
    }
  }
  if (column.rows) {
    for (const row of column.rows) {
      count += getRowItemCount(row, includeSectionsData);
    }
  }
  if (column.includeSection) {
    const nestedSection = includeSectionsData?.get(column.includeSection.slug);
    if (nestedSection) {
      for (const row of nestedSection.rows) {
        count += getRowItemCount(row, includeSectionsData);
      }
    }
  }
  return count;
}

/**
 * Calculate total items consumed by a row's columns
 * Based on component loop_count (not data_binding.limit - that's for API calls)
 */
function getRowItemCount(row: {
  columns?: Column[];
}, includeSectionsData?: Map<string, Section>): number {
  let count = 0;
  if (row.columns) {
    for (const col of row.columns) {
      count += getColumnItemCount(col, includeSectionsData);
    }
  }
  return count;
}
function RowRenderer({
  row,
  collectionsData,
  dynamicTabsMap,
  includeSectionsData,
  includeSectionApiData,
  isLastRow,
  parentColumnData,
  dataStartIndex = 0,
  isFirstRow = false
}: RowRendererProps) {
  // Skip hidden rows
  if (row.hidden) {
    return null;
  }

  // Get items for this row's data binding
  // Priority: 1. This row's own data_binding (resets index), 2. Parent column's data (continues index)
  const rawRowDataBindingKey = row.data_binding?.api_endpoint || row.data_binding?.collection_id;
  // Normalize the endpoint to match how data is stored in collectionsData
  const rowDataBindingKey = rawRowDataBindingKey ? normalizeApiEndpoint(rawRowDataBindingKey) : undefined;
  const hasOwnDataBinding = !!rowDataBindingKey;
  const rawItems = rowDataBindingKey ? collectionsData.get(rowDataBindingKey) || [] : parentColumnData || [];

  // Filter out metadata items BEFORE slicing (metadata items have _isCollectionMeta flag)
  const rowItems = rawItems.filter(item => !item._isCollectionMeta);
  if (rowDataBindingKey && rowItems.length === 0) {
    console.error(`[RowRenderer] WARNING: Row ${row.id} has data_binding "${rowDataBindingKey}" but 0 items! Available keys: ${Array.from(collectionsData.keys()).join(', ')}`);
  }

  // DEBUG logging removed - was causing issues in Firefox/Safari

  // If row has its own data_binding, start from 0; otherwise continue from parent's index
  const startOffset = hasOwnDataBinding ? 0 : dataStartIndex;

  // Render columns with sequential data offsets
  let runningOffset = startOffset;

  // Check if any column has sticky — stretch containers so sticky has room to work
  const hasStickyColumn = row.columns.some(col => col.sticky);

  // The LCP image is the lead story's hero, which lives in the row's WIDEST
  // column (col-span-7/8) — usually NOT column 0, since narrow side rails render
  // first in DOM. Giving priority to column 0 meant the preload went to a small
  // rail thumbnail while the real LCP image loaded at default priority.
  // (kannada-prabha RowRenderer parity.)
  const columnSpan = (col_0: Column) => col_0.width?.desktop ?? col_0.width?.tablet ?? col_0.width?.mobile ?? 12;
  let heroColumnIndex = 0;
  for (let k = 1; k < row.columns.length; k += 1) {
    if (columnSpan(row.columns[k]) > columnSpan(row.columns[heroColumnIndex])) {
      heroColumnIndex = k;
    }
  }
  return <div className={hasStickyColumn ? 'flex-1 flex flex-col' : ''}>
      {/* Row heading */}
      {row.heading?.enabled && <SectionHeading heading={row.heading} />}

      <div className={`grid grid-cols-12${hasStickyColumn ? ' flex-1' : ''}`}>
        {row.columns.map((column, index) => {
        const itemCount = getColumnItemCount(column, includeSectionsData);
        // Limit priority to the first row's WIDEST column only. Each
        // next/image with priority emits a separate <link rel="preload">,
        // and SEO audits flag >2 image preloads as bandwidth-wasteful.
        const columnPriority = isFirstRow && index === heroColumnIndex;
        // NO <Suspense> wrapper here (kannada-prabha parity). Nothing in the
        // column tree actually suspends — but wrapping each column in
        // Suspense made React "outline" every completed boundary bigger than
        // its progressive chunk size: the HTML shipped an empty fallback div
        // per column plus the real content as a hidden chunk + $RC swap
        // script (45 outlined boundaries on home). The page painted as blank
        // reserved boxes that popped in one after another as the ~4.5MB
        // document parsed ("blink"), and stayed blank entirely without JS.
        // Rendering the column directly streams its HTML in document order.
        const element = <ColumnRenderer key={column.id} column={column} collectionsData={collectionsData} dynamicTabsMap={dynamicTabsMap} includeSectionsData={includeSectionsData} includeSectionApiData={includeSectionApiData} isLastColumn={index === row.columns.length - 1} parentColumnData={rowItems} dataStartIndex={runningOffset} priority={columnPriority} />;
        runningOffset += itemCount;
        return element;
      })}
      </div>
    </div>;
}

// Memoized: below-fold rows share a mutable collectionsData/dynamicTabsMap
// reference that grows in place as new batches load (see BelowFoldContentLazy).
// Without this, every new batch re-renders every already-rendered row too,
// which shows up as already-visible cards flickering while unrelated content
// is still loading in.
export default memo(RowRenderer);